import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  CustomerService,
  TransactionCategoryEnum,
  TransactionCategoryLabelEnum,
  TransactionStatusEnum,
  TransactionTypes,
  WalletFeesTypes,
  WalletService,
  WalletTransaction,
} from '@cai-services';
import { WalletUtil } from '../../../../utils';
import { SessionService } from '../../../../core/_services/session.service';
import { APP_PROPS } from '../../../../cai-common.module';
import { ApplicationProperties } from '../../../../core/_base/layout/models/app-properties.model';
import { AwbFormatter } from '../../../../core/_base/layout/pipes/awb-formatter.pipe';
import { from, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
  selector: 'kt-latest-transactions',
  templateUrl: './latest-transactions.component.html',
  styleUrls: ['./latest-transactions.component.scss'],
  providers: [AwbFormatter],
  standalone: false,
})
export class LatestTransactionsComponent implements OnInit, OnDestroy {
  show = false;
  officeChangedStatusSub: any;
  latestRequestTimestamp: number;
  constructor(
    @Inject(APP_PROPS)
    private readonly appProperties: ApplicationProperties,
    private readonly walletService: WalletService,
    private readonly sessionService: SessionService,
    private readonly customerService: CustomerService,
    private readonly ref: ChangeDetectorRef,
    private readonly awbFormatter: AwbFormatter,
  ) {}
  private destroy$ = new Subject<void>();
  getCurrencySymbol = WalletUtil.getCustomCurrencyCode;
  transactionsList: Array<WalletTransaction>;
  get isCargoWallet(): boolean {
    return this.appProperties.name === 'cargowallet';
  }
  get walletBaseUrl(): string {
    return this.appProperties.walletBaseUrl;
  }
  ngOnInit(): void {
    this.officeChangedStatusSub = this.sessionService.officeChangedStatusObs
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => from(this.getTransactions())),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.officeChangedStatusSub) {
      this.officeChangedStatusSub.unsubscribe();
    }
  }

  async getTransactions(): Promise<void> {
    const requestTimestamp = Date.now();
    this.latestRequestTimestamp = requestTimestamp;

    try {
      const office = this.sessionService.getSelectedOffice(),
        officeId = office?.id?.toString(),
        excludedTransactionTypes: WalletFeesTypes[] = [
          WalletFeesTypes.ExportFee,
          WalletFeesTypes.LaterPaytoCargoFee,
          WalletFeesTypes.ExportFee,
          WalletFeesTypes.BookingFee,
          WalletFeesTypes.ConversionFee,
          WalletFeesTypes.TazapayTransactionFee,
          WalletFeesTypes.CargoaiTransactionFee,
          WalletFeesTypes.LaterPayTo,
          WalletFeesTypes.PrepaidPayToCargoFee,
          WalletFeesTypes.LaterPaytoCargoFee,
          WalletFeesTypes.TopUpFee,
          WalletFeesTypes.PayOutFee,
          WalletFeesTypes.PaymentFee,
          WalletFeesTypes.PrepaidFee,
          WalletFeesTypes.PrepaidReconciliationFee,
        ],
        WhiteListedTransactions: TransactionStatusEnum[] = [
          TransactionStatusEnum.SETTLED,
        ];

      if (officeId) {
        let getEventResponse;
        if (this.isCargoWallet) {
          getEventResponse = await this.customerService.getTransactionsList(
            office.walletId,
          );
        } else {
          getEventResponse =
            await this.walletService.getTransactionsList(officeId);
        }

        if (this.latestRequestTimestamp !== requestTimestamp) return;

        let transactionsList = getEventResponse.events
          ?.filter(
            (x) =>
              WhiteListedTransactions.includes(
                x.transaction_status as TransactionStatusEnum,
              ) &&
              !excludedTransactionTypes.includes(
                x.transaction_type as WalletFeesTypes,
              ) &&
              !(this.isGetPaidFee(x) || this.isPrepaidFee(x)),
          )
          .slice(0, 10)
          .map((transaction) => ({
            ...transaction,
            additional_info: transaction.is_conversion_transaction
              ? ` - Original amount ${transaction.conversion_data?.converted_amount} ${transaction.conversion_data?.converted_currency}`
              : '',
            tran_type: WalletUtil.getTransactionType(transaction),
          }));

        const removedTransactions = transactionsList?.filter(
          (transaction) =>
            transaction.transaction_type === TransactionTypes.Prepaid_PayTo &&
            transaction.transaction_category ===
              TransactionCategoryEnum.CURRENCY_CONVERSION,
        );

        transactionsList = transactionsList?.filter(
          (transaction) =>
            !(
              transaction.transaction_type === TransactionTypes.Prepaid_PayTo &&
              transaction.transaction_category ===
                TransactionCategoryEnum.CURRENCY_CONVERSION
            ),
        );

        const transactionMap = new Map<string, WalletTransaction>();

        transactionsList?.forEach((transaction) => {
          const key = `${transaction.reference_id}_${transaction.operation}_${transaction.transaction_category}`;
          transactionMap.set(key, transaction);
        });

        removedTransactions?.forEach((removedTransaction) => {
          const key = `${removedTransaction.reference_id}_CREDIT_CURRENCY_CONVERSION`;
          const matchedTransaction = transactionMap.get(key);
          if (matchedTransaction) {
            matchedTransaction.converted_from =
              removedTransaction?.conversion_data?.converted_currency ||
              removedTransaction.currency;
            matchedTransaction.operation = 'EXCHANGE';
          }
        });
        if (this.latestRequestTimestamp === requestTimestamp) {
          this.transactionsList = transactionsList;
          this.ref.detectChanges();
        }
      }
    } catch (err) {
      console.error('Error getting transactions list', err);

      if (this.latestRequestTimestamp === requestTimestamp) {
        this.transactionsList = [];
        this.ref.detectChanges();
      }
    }
  }
  isPrepaidTransaction(item: any): boolean {
    return (
      (item.transaction_type === TransactionTypes.Prepaid_PayTo ||
        item.transaction_type === 'PREPAID_PAY_FROM') &&
      item.transaction_category !== TransactionCategoryEnum.CURRENCY_CONVERSION
    );
  }

  isCurrencyConversionTransaction(item: any): boolean {
    return (
      item.transaction_type === 'PREPAID_PAY_FROM' &&
      item.transaction_category === TransactionCategoryEnum.CURRENCY_CONVERSION
    );
  }

  isTazaPay(item: any): boolean {
    return (
      item.transaction_type === 'TAZAPAY_PAY_TO' ||
      item.transaction_type === 'TAZAPAY_PAY_FROM'
    );
  }

  isPrepaidFee(item: any): boolean {
    return item.transaction_type == 'PREPAID_CARGOAI_FEE';
  }

  isGetPaidFee(item: any): boolean {
    return (
      item.metadata_user?.data?.fee_type === 'standard_fee' &&
      item.transaction_type === 'GET_PAID'
    );
  }

  isPrepaidBooking(item: any): boolean {
    return item.transaction_type === 'PREPAID_BOOKING';
  }
  isGetPaid(item: any): boolean {
    return item.transaction_type === 'GET_PAID';
  }
  getPaymentDirection(item: any): string {
    return item.transaction_type === TransactionTypes.Prepaid_PayTo
      ? 'Issued to'
      : 'Received from';
  }

  getTazaPayPayment(item: any): string {
    return item.operation === 'CREDIT'
      ? 'Payment Received From '
      : 'Payment Issued To';
  }

  getReferenceDetails(item: any): string {
    return this.referenceType(item) && this.referenceType(item) !== undefined
      ? ` ${this.referenceType(item)}${this.referenceValue(item)}`
      : '';
  }

  getPrepaidReference(item: any): string {
    return this.PrepaidReferenceType(item) &&
      this.PrepaidReferenceType(item) !== undefined
      ? `${this.PrepaidReferenceType(item)}${this.PrepaidReferenceValue(item)}`
      : '';
  }
  PrepaidReferenceType(item: WalletTransaction): string {
    if (item.transaction_type === TransactionTypes.Prepaid_PayTo) {
      return $localize`:@@wallet.payment-received: `;
    } else if (item.transaction_type === 'PREPAID_PAY_FROM') {
      return $localize`:@@wallet.payment-issued: `;
    }
  }

  BookingReferenceType(item: WalletTransaction): string {
    if (item.transaction_type === 'PREPAID_BOOKING') {
      return this.awbFormatter?.transform(WalletUtil.getMetadata(item, 'awb'));
    }
  }

  GetPaidReferenceValue(item: WalletTransaction): string {
    const buyerOffices = item.metadata_user?.data?.buyer_office,
      vendorOffices = item.metadata_user?.data?.office;

    if (!buyerOffices?.office_id && !vendorOffices?.office_id) {
      return item?.destination_pay_from;
    }

    if (
      item.transaction_type === 'GET_PAID' &&
      item.transaction_status === 'SETTLED' &&
      item.operation === 'DEBIT'
    ) {
      return `${vendorOffices?.company_name} ${vendorOffices?.country?.country_code} ${vendorOffices?.office_id} `;
    }
    if (
      item.transaction_type === 'GET_PAID' &&
      item.transaction_status === 'SETTLED' &&
      item.operation === 'CREDIT'
    ) {
      if (!buyerOffices?.company_name) {
        return item?.metadata_user?.data?.buyer_email;
      }
      return `${buyerOffices?.company_name} ${buyerOffices?.country?.country_code} ${buyerOffices?.office_id}`;
    }

    if (
      item.transaction_type === 'TAZAPAY_PAY_TO' &&
      item.transaction_status === 'SETTLED' &&
      item.operation === 'DEBIT'
    ) {
      return `${vendorOffices?.company_name} ${vendorOffices?.country?.country_code} ${vendorOffices?.office_id} `;
    }
    if (
      item.transaction_type === 'TAZAPAY_PAY_FROM' &&
      item.transaction_status === 'SETTLED' &&
      item.operation === 'CREDIT'
    ) {
      return `${buyerOffices?.company_name} ${buyerOffices?.country?.country_code} ${buyerOffices?.office_id}`;
    }
  }

  PrepaidReferenceValue(item: WalletTransaction): string {
    const buyerOffices = item.metadata_user?.data?.buyer_office;
    const vendorOffices = item.metadata_user?.data?.office;
    if (item.transaction_type === TransactionTypes.Prepaid_PayTo) {
      if (!vendorOffices?.company_name) {
        return item.email_pay_to;
      }
      return `${vendorOffices.company_name} ${vendorOffices.country?.country_code} ${vendorOffices.office_id}`.trim();
    }

    if (item.transaction_type === 'PREPAID_PAY_FROM') {
      if (!buyerOffices?.company_name) {
        return item.email_pay_from;
      }
      return `${buyerOffices?.company_name} ${buyerOffices?.country?.country_code} ${buyerOffices?.office_id}`;
    }
  }

  referenceType(item: WalletTransaction): string {
    if (WalletUtil.getMetadata(item, 'receipt_number')) {
      return $localize`:@@wallet.top-up-receipt: `;
    } else if (WalletUtil.getMetadata(item, 'invoice_number')) {
      return $localize`:@@wallet.top-up-invoice:Invoice: `;
    } else if (item.metadata_user?.type === 'STANDALONE') {
      return item.transaction_category
        ? `${TransactionCategoryLabelEnum[item.transaction_category]}: `
        : '';
    }
    return '';
  }

  referenceValue(item: WalletTransaction): string {
    if (WalletUtil.getMetadata(item, 'receipt_number')) {
      return WalletUtil.getMetadata(item, 'receipt_number');
    }
    if (WalletUtil.getMetadata(item, 'invoice_number')) {
      return WalletUtil.getMetadata(item, 'invoice_number');
    }
    if (item.metadata_user?.type === 'STANDALONE') {
      return WalletUtil.getMetadata(item, 'reference');
    } else {
      return (
        this.awbFormatter?.transform(WalletUtil.getMetadata(item, 'awb')) || ''
      );
    }
  }
}
